import { Observable, take } from "rxjs";
import { TranslateService } from "@ngx-translate/core";
import { PopupType } from "src/app/shared/enums/popup-types";
import { showMessage } from "src/app/shared/utils/toast.popup";
import { FormBuilder, FormGroup, Validators } from "@angular/forms";
import { PackageService } from "src/app/shared/services/package.service";
import { ContractService } from "src/app/shared/services/contract.service";
import { ContractType, Package } from "src/app/shared/types/contract-types";
import { CreateContractType } from "src/app/shared/types/create-contract-type";
import { Component, EventEmitter, Input, OnInit, Output } from "@angular/core";
import { DomainNameValidators } from "src/app/shared/validators/domain-name.validator";
import { PasswordCustomValidators } from "src/app/shared/validators/password.validator";

@Component({
  selector: "app-contract-form",
  templateUrl: "./contract-form.component.html",
  standalone: false,
})
export class ContractFormComponent implements OnInit {
  @Input() contractId!: string;
  @Output() closeForm = new EventEmitter<null>();
  passwordVisible = false;

  public options$!: Observable<Package[]>;

  formGroup!: FormGroup;
  isLoadingContract = false;

  constructor(
    private domainValidators: DomainNameValidators,
    private contractService: ContractService,
    private packageService: PackageService,
    public translate: TranslateService,
    private fb: FormBuilder,
  ) {}

  ngOnInit(): void {
    this.options$ = this.packageService.getDefaultPackage();

    this.formGroup = this.fb.group(
      {
        email: ["", [Validators.required, Validators.email]],
        password: [
          "",
          [Validators.required, PasswordCustomValidators.passwordValidator],
        ],
        agencyName: ["", Validators.required],
        phone: ["", Validators.required],
        domainName: [null],
        createWithInstance: [true],
        tier: [null],
      },
      { validators: PasswordCustomValidators.checkPasswords() },
    );

    const domainControl = this.formGroup.get("domainName");
    const tierControl = this.formGroup.get("tier");

    domainControl?.setValidators([
      Validators.required,
      DomainNameValidators.patternValidator(),
    ]);
    domainControl?.setAsyncValidators([
      this.domainValidators.domainAvailabilityValidator(),
    ]);

    tierControl?.setValidators([Validators.required]);

    domainControl?.updateValueAndValidity();

    this.options$.pipe(take(1)).subscribe((options) => {
      if (options?.length) {
        this.formGroup.get("tier")?.setValue(options[options.length - 1].id);
      }
    });
  }

  validateDomain() {
    const control = this.formGroup.get("domainName");
    if (!control)
      return;

    control.setAsyncValidators(
      this.domainValidators.domainAvailabilityValidator(),
    );
    control.updateValueAndValidity({ onlySelf: true, emitEvent: false });
  }

  resetForm(): void {
    this.formGroup.reset({
      createWithInstance: false,
    });
  }

  editContract(contract: ContractType) {
    this.formGroup.patchValue(contract);
  }

  submitForm(): void {
    if (this.formGroup.valid) {
      this.addContract();
    }
    else {
      this.formGroup.markAllAsTouched();
    }
  }

  addContract() {
    if (this.formGroup.valid) {
      this.isLoadingContract = true;
      const contractBody = this.formGroup.value as CreateContractType;

      const createSubscription = this.contractService
        .createContract(contractBody)
        .subscribe({
          next: () => {
            createSubscription.unsubscribe();
            showMessage(
              PopupType.Success,
              `${this.translate.instant("valid_contract")}`,
            );

            this.resetForm();
            this.isLoadingContract = false;
            this.closeForm.emit();
          },
          error: () => {
            createSubscription.unsubscribe();
            this.isLoadingContract = false;
            showMessage(
              PopupType.Danger,
              `${this.translate.instant("invalid_contract")}`,
            );
          },
        });
    }
    else {
      showMessage(
        PopupType.Danger,
        `${this.translate.instant("invalid_contract")}`,
      );
    }
  }

  close() {
    this.closeForm.emit();
  }

  togglePasswordVisibility() {
    this.passwordVisible = !this.passwordVisible;
  }
}
